import { z, ZodType } from "zod";
import { emailSchema, passwordSchema, phoneSchema } from "../schemas/common.schema";
import { CreateTextSchemaParams, SchemaType, TextSchemaSize } from "../../types/helpers.type";
import { TextSchemaSizeEnum } from "../../types/create-schema.type";
import { Schema } from "../../types/schema.type";

/**
 * Predefined maximum character limits for different text field sizes
 * @property {number} sm - Small text fields (50 chars)
 * @property {number} md - Medium text fields (200 chars)
 * @property {number} lg - Large text fields (1000 chars)
 */
const sizeMap = {
    /** For short inputs like names, titles (50 characters) */
    sm: 50,
    /** For medium inputs like descriptions (200 characters) */
    md: 200,
    /** For long form content like comments (1000 characters) */
    lg: 1000,
}

/**
 * Creates a required Zod string schema with basic validation rules
 * 
 * @param minLength - Minimum length requirement for the string (defaults to 1)
 * @param maxLength - Maximum length requirement for the string (defaults to sizeMap.sm)
 * @returns A Zod string schema with the following validations:
 *   - Trims whitespace from both ends
 *   - Enforces minimum length (defaults to 1)
 *   - Enforces maximum length (defaults to 50)
 */
const createRequiredSchema = (
    minLength?: number,
    maxLength?: number
) => z.string()
    .trim()
    .min(minLength || 1, "Required")
    .max(maxLength || sizeMap.sm);
    

/**
 * Returns a specific Zod schema based on the provided schema type and options.
 * Handles different types of validation schemas including text, email, phone,
 * date, boolean, and number fields.
 * 
 * @param options - Configuration object containing schema type and validation rules
 * @param options.type - The type of schema to create:
 *   - text: String validation with length constraints
 *   - email: Email format validation
 *   - phone: Phone number format validation
 *   - date: Date format validation
 *   - password: Password requirements validation
 *   - boolean: True/false validation
 *   - number: Numeric validation with optional range
 * @param options.min - Minimum value for number schemas
 * @param options.max - Maximum value for number schemas
 * @param options.minLength - Minimum length for text schemas
 * @param options.maxLength - Maximum length for text schemas
 * @returns A configured Zod schema based on the specified type
 */
const getSchema = (options: CreateTextSchemaParams) => {
    switch (options.type) {
        case SchemaType.email:
            return emailSchema;

        case SchemaType.phone:
            return phoneSchema;

        case SchemaType.date:
            return passwordSchema;

        case SchemaType.boolean:
            return z.boolean();

        case SchemaType.number:
            const numberSchema = z.number()
            if (options.min) {
                numberSchema.min(options.min);
            }
            if (options.max) {
                numberSchema.max(options.max);
            }
            return numberSchema;

        case SchemaType.text:
            return createRequiredSchema(
                options.minLength,
                options.maxLength
            )

        case SchemaType.password:
            return passwordSchema;

        case SchemaType.textArea:
            return createRequiredSchema(
                options.minLength,
                options.maxLength || sizeMap.lg
            )
        case SchemaType.array:
            return z.array(
                z.enum(
                    options
                    .options.map(option => option.value) as [
                        typeof options.options[number]['value'], 
                        ...typeof options.options[number]['value'][]
                    ]
                )
            )
    }
}

/**
 * Creates a Zod schema with predefined validation rules based on the provided parameters.
 * This is the main entry point for creating validation schemas with common configurations.
 * 
 * @param params - Configuration object for the schema
 * @param params.size - Controls the maximum length of the string:
 *   - "sm": 50 characters (default) - For names, titles, short references
 *   - "md": 200 characters - For addresses, brief descriptions
 *   - "lg": 1000 characters - For detailed descriptions, comments
 * @param params.required - Determines if the field is required (default: true)
 * @param params.minLength - Minimum length requirement (default: 1)
 * @param params.maxLength - Custom maximum length (overrides size parameter)
 * @param params.type - Input field type:
 *   - "text" (default) - General text input
 *   - "email" - Email validation
 *   - "phone" - Phone number validation
 *   - "date" - Date validation
 *   - "password" - Password validation
 *   - "boolean" - Boolean validation
 *   - "number" - Number validation with optional min/max
 * @returns A configured Zod schema with appropriate validations
 * 
 * @example
 * // Required short text field (max 50 chars)
 * const nameSchema = createTextSchema({ size: "sm" });
 * 
 * // Optional medium text field (max 200 chars)
 * const descriptionSchema = createTextSchema({ size: "md", required: false });
 * 
 * // Email validation
 * const emailSchema = createTextSchema({ type: "email" });
 * 
 * // Number with range validation
 * const ageSchema = createTextSchema({ type: "number", min: 0, max: 120 });
 */
export const createSchema = (params?: CreateTextSchemaParams) => {
    const {
        required = true,
    } = params || {};

    const type = SchemaType.text

    if (!required) {
        return getSchema({
            type,
            ...params
        }).optional()
    }

    return getSchema({
        type,
        ...params
    });
}


/**
 * Extracts the values from an array of options,
 * and make it accessible for zod enum to parse
 * @param options - The array of options to extract values from
 * @returns An array of values from the options
 */
export function getOptionsValues<T, V>(options: readonly{label: T, value: V}[]) {
    const optionsValues = options.map(option => option.value) as [
        V,
        ...V[]
    ]
    return optionsValues
}


// type CreateText2SchemaProps = {
//     min?: number,
//     max?: number,
//     size?: TextSchemaSizeEnum,
//     minErrorMessage?: string,
//     maxErrorMessage?: string,
// } 


// const createText2Schema = (props: CreateText2SchemaProps) => {
//     const min = props.min || 1
//     const max = props.max || TextSchemaSizeEnum.sm
//     const minErrorMessage = props.minErrorMessage || `Min ${min} characters`
//     const maxErrorMessage = props.maxErrorMessage || `Max ${max} characters`

//     const schema =  z.string()
//         .min(min, minErrorMessage)
//         .max(max, maxErrorMessage)
    
//     if(!propsrequired) {
//         return schema.optional()
//     }

//     return schema
// }


export const transformPhoneNumber = (value: string) => {
    return value.replace(/\D/g, "")
}

// export function makeOptional<T>(schema: Schema<T>, schemaType: 'string'): Schema<string | null | undefined>
// export function makeOptional<T>(schema: Schema<T>, schemaType: 'number'): Schema<number | null | undefined>
// export function makeOptional<T>(schema: Schema<T>, schemaType: 'other'): Schema<unknown | null | undefined>

// export function makeOptional<T>(schema: Schema<T>, schemaType: 'string') {
//     return schema
//         .nullable()
//         .optional()
//         .or(schemaType === 'string' ? z.literal('') : schemaType === 'number' ? z.literal(0) : z.undefined());
// }
// export function makeOptional<T>(schema: Schema<T>, schemaType: 'string' | 'number' | 'other' = 'string') {
//     return schema
//         .nullable()
//         .optional()
//         .or(schemaType === 'string' ? z.literal('') : schemaType === 'number' ? z.literal(0) : z.undefined());
// }
export function makeOptional<T>(schema: Schema<T>): 
    Schema<T | null | undefined>
export function makeOptional<T>(schema: Schema<T>, schemaType: 'number'): 
    Schema<number | null | undefined>
export function makeOptional<T>(schema: Schema<T>, schemaType: 'string'): 
    Schema<T | null | undefined>
export function makeOptional<T>(schema: Schema<T>, schemaType: 'array'): 
    Schema<T | null | undefined>
export function makeOptional<T>(schema: Schema<T>, schemaType: 'boolean'): 
    Schema<T | null | undefined>
export function makeOptional<T>(schema: Schema<T>, schemaType: 'date'): 
    Schema<T | null | undefined>

export function makeOptional<T>(
    schema: ZodType<T>,
    schemaType?: "number" | "string" | "array" | "boolean" | "date"
) {
    if (schemaType === "number") {
        return schema.nullable().optional().or(z.literal(0));
    }
    if (schemaType === "boolean") {
        return schema.nullable().optional();
    }
    if (schemaType === "array") {
        return schema.nullable().optional().or(z.array(z.literal("")));
    }
    // For dates, allow null, undefined, empty string, or valid date
    // The dateSchema already handles Date objects and date strings
    if (schemaType === "date") {
        return z.preprocess(
            (val) => {
                // Convert empty strings to null for dates
                if (val === "" || (typeof val === "string" && val.trim() === "")) {
                    return null;
                }
                return val;
            },
            z.union([
                z.null(),
                z.undefined(),
                schema
            ])
        ).optional();
    }
    // For strings, preprocess whitespace-only strings to null to match database pattern
    // Then use a union that allows null, undefined, empty string, or valid strings
    // This preserves null values and handles edge cases like "\n" in the database
    return z.preprocess(
        (val) => {
            // Convert whitespace-only strings (like "\n", " ", etc.) to null
            // This matches the database pattern where most entries are null
            if (typeof val === "string" && val.trim() === "") {
                return null;
            }
            return val;
        },
        z.union([
            z.null(),
            z.undefined(),
            z.literal(""),
            schema
        ])
    ).optional();
}


/**
 * Type definition for the makeOptional function
 * @param schema - The schema to make optional
 * @param schemaType - The type of schema ('string', 'number', or 'other')
 * @returns A schema that accepts the original type, null, or undefined
 */



// export type MakeOptionalProps<T> = (
//     schema: Schema<T>, schemaType?: 'string' | 'number' | 'other') => Schema<T | null | undefined>;